import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  Star as StarIcon,
  Download as DownloadIcon,
  Brush as BrushIcon,
  Palette as PaletteIcon,
  Code as CodeIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface HeroSectionProps {
  onExploreClick?: () => void;
  onSearchClick?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onExploreClick, onSearchClick }) => {
  const navigate = useNavigate();
  
  const featuredCategories = [
    { name: 'Icons', count: '2.5k+', color: '#1976d2', icon: <DownloadIcon /> },
    { name: 'Logos', count: '1.8k+', color: '#42a5f5', icon: <BusinessIcon /> },
    { name: 'Illustrations', count: '3.2k+', color: '#9c27b0', icon: <BrushIcon /> },
    { name: 'Graphics', count: '2.1k+', color: '#ff9800', icon: <PaletteIcon /> }
  ];

  const handleExploreClick = () => {
    if (onExploreClick) {
      onExploreClick();
    } else {
      navigate('/products');
    }
  };

  const handleTrendingClick = () => {
    if (onSearchClick) {
      onSearchClick();
    } else {
      navigate('/products');
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', py: { xs: 2, md: 3 } }}>
      <Container maxWidth="lg">
        {/* Main Hero Content */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
          {/* Hero Logo - REMOVED */}
          
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' }
            }}
          >
            Discover Amazing SVG Designs
          </Typography>
          
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ 
              mb: 3, 
              maxWidth: 600, 
              mx: 'auto', 
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.5rem' }
            }}
          >
            High-quality vector graphics, icons, and illustrations for your next project. 
            Download instantly after purchase with commercial licensing included.
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, md: 2 }, 
            justifyContent: 'center', 
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleExploreClick}
              startIcon={<SearchIcon />}
              sx={{
                px: { xs: 3, md: 4 },
                py: { xs: 1, md: 1.5 },
                fontSize: { xs: '1rem', md: '1.1rem' },
                fontWeight: 600,
                borderRadius: 2
              }}
            >
              Explore Collection
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleTrendingClick}
              startIcon={<TrendingIcon />}
              sx={{
                px: { xs: 3, md: 4 },
                py: { xs: 1, md: 1.5 },
                fontSize: { xs: '1rem', md: '1.1rem' },
                fontWeight: 600,
                borderRadius: 2
              }}
            >
              Trending Now
            </Button>
          </Box>
        </Box>

        {/* Stats Section */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }} justifyContent="center">
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  color: 'primary.main',
                  fontSize: { xs: '1.8rem', md: '3rem' }
                }}>
                  10k+
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
                  SVG Designs
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  color: 'primary.main',
                  fontSize: { xs: '1.8rem', md: '3rem' }
                }}>
                  50k+
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
                  Downloads
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  color: 'primary.main',
                  fontSize: { xs: '1.8rem', md: '3rem' }
                }}>
                  4.9
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
                  Average Rating
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  color: 'primary.main',
                  fontSize: { xs: '1.8rem', md: '3rem' }
                }}>
                  24/7
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
                  Support
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Featured Categories */}
        <Box>
          <Typography
            variant="h4"
            sx={{ 
              textAlign: 'center', 
              mb: { xs: 3, md: 4 }, 
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            Popular Categories
          </Typography>
          
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {featuredCategories.map((category) => (
              <Grid item xs={6} sm={6} md={3} key={category.name}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/products')}
                >
                  <CardContent sx={{ textAlign: 'center', py: { xs: 2, md: 3 } }}>
                    <Box
                      sx={{
                        width: { xs: 50, md: 60 },
                        height: { xs: 50, md: 60 },
                        borderRadius: '50%',
                        bgcolor: category.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2
                      }}
                    >
                      {React.cloneElement(category.icon, { 
                        sx: { 
                          color: 'white', 
                          fontSize: { xs: 24, md: 28 } 
                        } 
                      })}
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: { xs: '0.9rem', md: '1.25rem' }
                    }}>
                      {category.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                      {category.count} designs
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Featured Preview */}
        <Box sx={{ mt: 6 }}>
          <Typography
            variant="h4"
            sx={{ textAlign: 'center', mb: 4, fontWeight: 600 }}
          >
            Why Choose ArtFul?
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}
              >
                <StarIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Premium Quality
                </Typography>
                <Typography variant="body2">
                  Every design is carefully crafted and reviewed by our expert team to ensure the highest quality standards.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white'
                }}
              >
                <DownloadIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Instant Download
                </Typography>
                <Typography variant="body2">
                  Get your files immediately after purchase with our secure and fast download system.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white'
                }}
              >
                <CodeIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Commercial License
                </Typography>
                <Typography variant="body2">
                  All designs come with commercial licensing, so you can use them in your projects without restrictions.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection; 